import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  formatDate,
  HelpTip,
  PersonDisplay,
} from '@dataroom/shared';
import type {
  AdminSigningEnvelope,
  RoomDocument,
  SigningEnvelopeStatus,
  SigningEnvelopeType,
} from '@dataroom/shared';

interface AgreementsCardProps {
  envelopes: AdminSigningEnvelope[];
  loading: boolean;
  error: string | null;
  onIssue: () => void;
  /** The room's documents, to pick / show the master NDA. */
  documents: RoomDocument[];
  /** The room's current master NDA document id, or null when unset. */
  masterNdaDocumentId: string | null;
  /** Set (a document id) or clear (null) the room's master NDA. */
  onSetMasterNda: (documentId: string | null) => void;
  masterNdaPending: boolean;
  masterNdaError: string | null;
  /** Opens the detail view for a clicked issued-agreement row. */
  onSelectEnvelope: (envelope: AdminSigningEnvelope) => void;
}

/** The master-NDA setter row: shows the current master NDA (or "not set") with an
 * inline picker to choose a room document as the master, or clear it. */
function MasterNdaRow({
  documents,
  masterNdaDocumentId,
  onSetMasterNda,
  pending,
  error,
}: {
  documents: RoomDocument[];
  masterNdaDocumentId: string | null;
  onSetMasterNda: (documentId: string | null) => void;
  pending: boolean;
  error: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(masterNdaDocumentId ?? documents[0]?.id ?? '');
  const current = documents.find((d) => d.id === masterNdaDocumentId) ?? null;

  const save = (documentId: string | null) =>
    onSetMasterNda(documentId);

  return (
    <div className="mb-4 rounded-md border bg-muted/30 p-3 text-sm">
      {editing ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1">
            <span className="font-medium">Master NDA:</span>
            <HelpTip id="admin.agreements.master-nda" className="ml-1" />
          </span>
          {documents.length > 0 ? (
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              disabled={pending}
            >
              {documents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-muted-foreground">Upload a document to the room first.</span>
          )}
          <Button size="sm" disabled={pending || !pick} onClick={() => save(pick)}>
            {pending ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="ghost" size="sm" disabled={pending} onClick={() => setEditing(false)}>
            Cancel
          </Button>
          {current ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={pending}
              onClick={() => save(null)}
              className="text-destructive"
            >
              Clear
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1">
            <span className="font-medium">Master NDA:</span>
            <HelpTip id="admin.agreements.master-nda" className="ml-1" />
          </span>
          <span className={current ? '' : 'text-muted-foreground'}>
            {current ? current.name : 'Not set'}
          </span>
          <span className="text-muted-foreground">·</span>
          <button
            type="button"
            className="font-medium text-primary hover:underline"
            onClick={() => {
              setPick(masterNdaDocumentId ?? documents[0]?.id ?? '');
              setEditing(true);
            }}
          >
            {current ? 'Change' : 'Set'}
          </button>
        </div>
      )}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
      {!current ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Set the room&apos;s NDA once; issuing an agreement to a buyer uses it by default.
        </p>
      ) : null}
    </div>
  );
}

const TYPE_LABELS: Record<SigningEnvelopeType, string> = {
  nda: 'NDA',
  nc: 'Non-circumvention',
};

const STATUS_LABELS: Record<SigningEnvelopeStatus, string> = {
  created: 'Issued — awaiting signer',
  consent_recorded: 'Consent given',
  signed: 'Signed',
  sealed: 'Sealed',
};

const STATUS_STYLES: Record<SigningEnvelopeStatus, string> = {
  created: 'bg-muted text-muted-foreground',
  consent_recorded: 'bg-st-check text-st-check-fg',
  signed: 'bg-st-processing text-st-processing-fg',
  sealed: 'bg-st-certified text-st-certified-fg',
};

/**
 * PRD-0004 §3c NDA management — the room's NC/NDA agreements: who was issued
 * what, and who has signed. Read view (issuing is a separate flow). The
 * envelope walk (consent → sign → seal) is buyer-driven; this shows the admin
 * where each signer is. A `sealed` NDA is what advances the buyer past
 * nda_signed and unlocks the CIM.
 */
export function AgreementsCard({
  envelopes,
  loading,
  error,
  onIssue,
  documents,
  masterNdaDocumentId,
  onSetMasterNda,
  masterNdaPending,
  masterNdaError,
  onSelectEnvelope,
}: AgreementsCardProps) {
  const hasMasterNda = documents.some((d) => d.id === masterNdaDocumentId);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>Agreements (NDA)</CardTitle>
            <p className="text-sm text-muted-foreground">
              Who has been issued the NDA, and how far they are through signing it.
              Sealing an NDA unlocks the CIM.
            </p>
          </div>
          <Button size="sm" onClick={onIssue} disabled={!hasMasterNda}>
            Issue agreement
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <MasterNdaRow
          documents={documents}
          masterNdaDocumentId={masterNdaDocumentId}
          onSetMasterNda={onSetMasterNda}
          pending={masterNdaPending}
          error={masterNdaError}
        />
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : envelopes.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 font-medium">Signer</th>
                <th className="py-2 font-medium">Agreement</th>
                <th className="py-2 font-medium">Status</th>
                <th className="py-2 font-medium">Issued</th>
              </tr>
            </thead>
            <tbody>
              {envelopes.map((env) => {
                const cancelled = env.cancelledAt !== null;
                return (
                  <tr
                    key={env.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectEnvelope(env)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectEnvelope(env);
                      }
                    }}
                    className="cursor-pointer border-b last:border-0 hover:bg-accent/50"
                  >
                    <td className="py-2 pr-4 font-medium">
                      <PersonDisplay email={env.signerEmail} fallback={env.signerActorId} size="sm" />
                    </td>
                    <td className="py-2 pr-4">{TYPE_LABELS[env.envelopeType]}</td>
                    <td className="py-2 pr-4">
                      {cancelled ? (
                        <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                          Cancelled
                        </span>
                      ) : (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[env.status]}`}
                        >
                          {STATUS_LABELS[env.status]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4">{formatDate(env.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-muted-foreground">
            No agreements issued yet. Issue an NDA to a buyer to start their signing walk.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
